import React from 'react'
import { MerchantLocation } from '@slerp/controls'
import { Section } from 'components/Common'
import { StepStatus } from 'hooks/useStep'
import {
  LinkStoresButton,
  SummaryActionCard,
  SummaryText,
  LoyaltyAndTableGroup
} from './styles'
import {
  LINK_STORES_STEP_TITLE,
  LINK_STORES_STEP_DESCRIPTION,
  linkedSummary
} from './copy'
import { findStoreLink, isLeatConnected } from './utils'
import { ConnectStoresRow, LeatStore, LeatStoreLink } from './types'
import PushOrdersCard from './PushOrdersCard'
import StoreLinkTable from './StoreLinkTable'
import { useStoreLinkRows } from './useStoreLinkRows'
import LoadingPrimaryButton from '../Common/LoadingPrimaryButton'

interface StoreListStepProps {
  activeStores: MerchantLocation[]
  links: LeatStoreLink[]
  leatShops: LeatStore[]
  loading?: boolean
  onSave: (rows: ConnectStoresRow[]) => void
  onOpenConnectForm: () => void
}

const StoreListStep = ({
  activeStores,
  links,
  leatShops,
  loading,
  onSave,
  onOpenConnectForm
}: StoreListStepProps) => {
  const connectedStores = activeStores.filter((store) =>
    isLeatConnected(store.id, links)
  )
  const unconnectedStores = activeStores.filter(
    (store) => !connectedStores.includes(store)
  )

  // Every store listed here is linked, so a store without a row yet starts
  // from its saved link — a blank default would unlink it on SAVE.
  const savedRow = (storeId: string): ConnectStoresRow => {
    const existing = findStoreLink(storeId, links)
    return {
      storeId,
      leatStoreId: existing?.store_integration_settings.leat_store_id ?? null,
      leatOrderPushEnabled:
        existing?.store_integration_settings.leat_order_push_enabled ?? true
    }
  }

  const { updateRow, getRow } = useStoreLinkRows(
    () =>
      connectedStores.reduce<Record<string, ConnectStoresRow>>((acc, store) => {
        acc[store.id] = savedRow(store.id)
        return acc
      }, {}),
    savedRow
  )

  const rowList = connectedStores.map((store) => getRow(store.id))
  // Both halves of the summary sentence read the live rows: counting links
  // from the saved `links` instead would keep claiming a store is linked
  // while its own row on screen already says None.
  const linkedCount = rowList.filter((row) => row.leatStoreId).length
  const pushDisabledCount = rowList.filter(
    (row) => !row.leatOrderPushEnabled
  ).length

  return (
    <>
      <Section
        title={LINK_STORES_STEP_TITLE}
        description={LINK_STORES_STEP_DESCRIPTION}
        stepStatus={StepStatus.completed}
        stepNumber={2}
      >
        <LoyaltyAndTableGroup>
          <PushOrdersCard />

          <StoreLinkTable
            stores={connectedStores}
            getRow={getRow}
            leatShops={leatShops}
            loading={loading}
            allowNone
            onRowChange={updateRow}
          />

          {unconnectedStores.length > 0 && (
            <LinkStoresButton disabled={loading} onClick={onOpenConnectForm}>
              LINK STORES
            </LinkStoresButton>
          )}
        </LoyaltyAndTableGroup>
      </Section>

      <SummaryActionCard>
        <SummaryText>
          {linkedSummary(linkedCount, activeStores.length, pushDisabledCount)}
        </SummaryText>
        <LoadingPrimaryButton
          disabled={loading}
          loading={loading}
          onClick={() => onSave(rowList)}
        >
          SAVE
        </LoadingPrimaryButton>
      </SummaryActionCard>
    </>
  )
}

export default StoreListStep
